import { ReloadOutlined } from '@ant-design/icons';
import type { InterfaceLogRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, DatePicker, Descriptions, Drawer, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

const PARTNERS = [
  { value: '', label: '全部接入方' },
  { value: 'HIS', label: 'HIS' },
  { value: 'SUPPLIER', label: '供应商系统' },
  { value: 'LOGISTICS', label: '物流' },
  { value: 'RES', label: '监管平台' },
  { value: 'PDA', label: 'PDA' }
];

/** 接口日志（ops_interface_log）：开放接口 / 设备上报 / 扫码 / 轨迹接入统一登记。 */
export default function InterfaceLogs() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [partner, setPartner] = useState('');
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [current, setCurrent] = useState<InterfaceLogRow | null>(null);
  const [size, setSize] = useState(50);

  const list = useQuery({
    queryKey: ['ops', 'interface-log', range, partner, status, keyword, size],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.opsExtras.interfaceLog.list({
        beginTime: range[0].startOf('day').toISOString(),
        endTime: range[1].endOf('day').toISOString(),
        partner: partner || undefined,
        status: status || undefined,
        keyword: keyword || undefined,
        size
      })
  });

  const rows = list.data?.items ?? [];

  const columns: ColumnsType<InterfaceLogRow> = [
    { title: '时间', dataIndex: 'createAt', width: 170, render: (value: string) => formatDisplayTime(value) },
    {
      title: '接入方',
      dataIndex: 'partner',
      width: 110,
      render: (value: string) => <Tag color={value === 'HIS' ? 'blue' : value === 'SUPPLIER' ? 'purple' : value === 'PDA' ? 'cyan' : 'default'}>{value}</Tag>
    },
    { title: '接口', dataIndex: 'interfaceCode', width: 260, ellipsis: true, render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
    { title: '状态', dataIndex: 'status', width: 100, render: (value: string) => <Tag color={value === 'SUCCESS' ? 'green' : 'red'}>{value}</Tag> },
    { title: 'HTTP', dataIndex: 'httpStatus', width: 90, align: 'right' },
    { title: '耗时', dataIndex: 'costMs', width: 90, align: 'right', render: (value: number) => `${value} ms` },
    { title: 'TraceId', dataIndex: 'traceId', width: 180, render: (value: string | null) => (value ? <Typography.Text copyable>{value}</Typography.Text> : '-') },
    { title: '错误', dataIndex: 'errorMsg', width: 180, ellipsis: true, render: (value: string | null) => value || '-' },
    {
      title: '操作',
      width: 80,
      render: (_, row) => (
        <Button type="link" onClick={() => setCurrent(row)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          接口日志
          <Typography.Text type="secondary">审计留存与对账举证（sys:audit:view）</Typography.Text>
        </Space>
      }
      extra={
        <Space wrap>
          <DatePicker.RangePicker value={range} onChange={(value) => value && setRange(value as [Dayjs, Dayjs])} allowClear={false} />
          <Select value={partner} options={PARTNERS} onChange={setPartner} style={{ width: 150 }} />
          <Select
            value={status}
            onChange={setStatus}
            style={{ width: 120 }}
            options={[
              { value: '', label: '全部状态' },
              { value: 'SUCCESS', label: '成功' },
              { value: 'FAIL', label: '失败' }
            ]}
          />
          <Input.Search allowClear placeholder="接口 / TraceId / 错误" style={{ width: 220 }} onSearch={(value) => setKeyword(value.trim())} />
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => list.refetch()}>
            刷新
          </Button>
        </Space>
      }
    >
      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="接口日志加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}
      <Table<InterfaceLogRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
      />
      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.data?.hasMore ? (
          <Button onClick={() => setSize((prev) => prev + 50)} loading={list.isFetching}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer title="接口调用详情" width={720} open={Boolean(current)} onClose={() => setCurrent(null)}>
        {current ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="接口">{current.interfaceCode}</Descriptions.Item>
            <Descriptions.Item label="接入方">{current.partner}</Descriptions.Item>
            <Descriptions.Item label="方向">{current.direction === 'IN' ? '入站' : '出站'}</Descriptions.Item>
            <Descriptions.Item label="URL">{current.requestUrl || '-'}</Descriptions.Item>
            <Descriptions.Item label="HTTP">{current.httpStatus}</Descriptions.Item>
            <Descriptions.Item label="耗时">{current.costMs} ms</Descriptions.Item>
            <Descriptions.Item label="TraceId">{current.traceId || '-'}</Descriptions.Item>
            <Descriptions.Item label="错误">{current.errorMsg || '-'}</Descriptions.Item>
            <Descriptions.Item label="请求体">
              <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{current.requestBody || '-'}</Typography.Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="响应体">
              <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{current.responseBody || '-'}</Typography.Paragraph>
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>
    </Card>
  );
}
